/**
 * Fixed Assets Service
 * Comprehensive fixed assets register with depreciation tracking
 * Compliant with FASB ASC 958 (Non-Profit) and IAS 16 (Property, Plant & Equipment)
 */

import { supabase } from '@/lib/supabase'

export type DepreciationMethod = 'straight_line' | 'declining_balance' | 'units_of_production' | 'none'
export type AssetCategory = 'land' | 'building' | 'vehicle' | 'furniture' | 'equipment' | 'computer' | 'musical_instrument' | 'other'
export type AssetStatus = 'active' | 'disposed' | 'under_maintenance' | 'lost' | 'stolen'
export type DisposalMethod = 'sold' | 'scrapped' | 'donated' | 'lost' | 'stolen'
export type PaymentMethod = 'cash' | 'check' | 'bank_transfer' | 'credit_card' | 'credit_purchase' | 'other'
export type PaymentStatus = 'paid' | 'partially_paid' | 'unpaid' | 'credit'

export interface FixedAsset {
  id: string
  church_id: string
  asset_code: string
  asset_name: string
  asset_category: AssetCategory
  asset_description?: string

  // Details
  serial_number?: string
  model_number?: string
  manufacturer?: string
  purchase_order_number?: string
  warranty_expiry_date?: string

  // Financial
  purchase_date: string
  purchase_cost: number
  salvage_value: number
  current_value: number

  // Depreciation
  depreciation_method: DepreciationMethod
  useful_life_years?: number
  useful_life_units?: number
  depreciation_rate?: number
  depreciation_start_date?: string
  accumulated_depreciation: number
  last_depreciation_date?: string

  // Location & Assignment
  location?: string
  department?: string
  assigned_to?: string

  // Vendor
  vendor_id?: string

  // Accounting
  asset_account_id: string
  depreciation_expense_account_id?: string
  accumulated_depreciation_account_id?: string

  // Payment Information
  payment_method?: PaymentMethod
  payment_status?: PaymentStatus
  payment_reference?: string
  payment_date?: string
  amount_paid: number
  amount_outstanding: number
  credit_terms?: string
  payment_due_date?: string
  purchase_journal_entry_id?: string
  bank_account_id?: string
  accounts_payable_id?: string

  // Disposal
  disposal_date?: string
  disposal_method?: DisposalMethod
  disposal_proceeds?: number
  disposal_notes?: string

  // Status
  status: AssetStatus
  is_active: boolean

  // Insurance
  is_insured: boolean
  insurance_policy_number?: string
  insurance_value?: number
  insurance_expiry_date?: string

  // Maintenance
  last_maintenance_date?: string
  next_maintenance_date?: string
  maintenance_notes?: string

  // Documents
  documents?: any
  photos?: any
  notes?: string

  // Audit
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}

export interface AssetSummary extends FixedAsset {
  assigned_to_name?: string
  vendor_name?: string
  asset_account_name?: string
  depreciation_expense_account_name?: string
  bank_account_name?: string
  payment_summary?: string
  age_years: number
  depreciation_percentage: number
}

export interface AssetPayment {
  id: string
  church_id: string
  asset_id: string
  payment_date: string
  payment_amount: number
  payment_method: PaymentMethod
  payment_reference?: string
  bank_account_id?: string
  journal_entry_id?: string
  notes?: string
  created_at: string
  created_by?: string
}

export interface DepreciationSchedule {
  id: string
  church_id: string
  asset_id: string
  depreciation_date: string
  fiscal_year: number
  fiscal_period: number
  opening_value: number
  depreciation_amount: number
  accumulated_depreciation: number
  closing_value: number
  journal_entry_id?: string
  status: 'scheduled' | 'posted' | 'reversed'
  created_at: string
  posted_at?: string
  posted_by?: string
}

export interface MaintenanceLog {
  id: string
  church_id: string
  asset_id: string
  maintenance_date: string
  maintenance_type: 'routine' | 'repair' | 'inspection' | 'upgrade'
  description: string
  performed_by?: string
  cost: number
  expense_id?: string
  next_maintenance_date?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  created_at: string
  created_by?: string
}

export class AssetsService {
  /**
   * Get all fixed assets for a church
   */
  static async getAssets(churchId: string, filters?: {
    status?: AssetStatus
    category?: AssetCategory
    assigned_to?: string
    search?: string
  }): Promise<AssetSummary[]> {
    let query = supabase
      .from('v_assets_register')
      .select('*')
      .eq('church_id', churchId)
      .order('asset_code')

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.category) {
      query = query.eq('asset_category', filters.category)
    }

    if (filters?.assigned_to) {
      query = query.eq('assigned_to', filters.assigned_to)
    }

    if (filters?.search) {
      query = query.or(`asset_name.ilike.%${filters.search}%,asset_code.ilike.%${filters.search}%,serial_number.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get a single asset by ID
   */
  static async getAsset(assetId: string): Promise<FixedAsset> {
    const { data, error } = await supabase
      .from('fixed_assets')
      .select('*')
      .eq('id', assetId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create a new fixed asset
   */
  static async createAsset(
    churchId: string,
    assetData: Omit<FixedAsset, 'id' | 'asset_code' | 'church_id' | 'current_value' | 'accumulated_depreciation' | 'created_at' | 'updated_at'>,
    userId: string
  ): Promise<FixedAsset> {
    // Generate asset code
    const { data: codeData } = await supabase.rpc('generate_asset_code', {
      p_church_id: churchId
    })

    const { data, error } = await supabase
      .from('fixed_assets')
      .insert({
        church_id: churchId,
        asset_code: codeData,
        ...assetData,
        current_value: assetData.purchase_cost, // Initially equal to purchase cost
        accumulated_depreciation: 0,
        created_by: userId,
        updated_by: userId
      })
      .select()
      .single()

    if (error) throw error

    // Create purchase journal entry if payment info provided
    if (assetData.payment_method && assetData.payment_method !== 'credit_purchase') {
      await this.createPurchaseJournalEntry(data, userId)
    } else if (assetData.payment_method === 'credit_purchase') {
      await this.createCreditPurchaseJournalEntry(data, userId)
    }

    // Generate depreciation schedule if applicable
    if (data.depreciation_method !== 'none' && data.useful_life_years) {
      await this.generateDepreciationSchedule(data.id, userId)
    }

    return data
  }

  /**
   * Update a fixed asset
   */
  static async updateAsset(
    assetId: string,
    assetData: Partial<FixedAsset>,
    userId: string
  ): Promise<FixedAsset> {
    const { data, error } = await supabase
      .from('fixed_assets')
      .update({
        ...assetData,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', assetId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Calculate monthly depreciation using straight-line method
   */
  static calculateStraightLineDepreciation(
    cost: number,
    salvageValue: number,
    usefulLifeYears: number
  ): number {
    if (usefulLifeYears === 0) return 0
    return (cost - salvageValue) / usefulLifeYears / 12
  }

  /**
   * Calculate monthly depreciation using declining balance method
   */
  static calculateDecliningBalanceDepreciation(
    currentValue: number,
    depreciationRate: number
  ): number {
    return currentValue * (depreciationRate / 100 / 12)
  }

  /**
   * Generate depreciation schedule for an asset
   */
  static async generateDepreciationSchedule(
    assetId: string,
    userId: string
  ): Promise<void> {
    // Get asset details
    const asset = await this.getAsset(assetId)

    if (asset.depreciation_method === 'none' || !asset.useful_life_years) {
      return
    }

    const startDate = asset.depreciation_start_date
      ? new Date(asset.depreciation_start_date)
      : new Date(asset.purchase_date)

    const monthlyDepreciation = this.calculateStraightLineDepreciation(
      asset.purchase_cost,
      asset.salvage_value,
      asset.useful_life_years
    )

    const scheduleEntries: any[] = []
    let accumulatedDep = asset.accumulated_depreciation
    let currentValue = asset.current_value

    // Generate schedule for remaining life
    const endDate = new Date(startDate)
    endDate.setFullYear(endDate.getFullYear() + asset.useful_life_years)

    let currentDate = new Date(startDate)

    while (currentDate <= endDate && currentValue > asset.salvage_value) {
      const depAmount = Math.min(monthlyDepreciation, currentValue - asset.salvage_value)

      scheduleEntries.push({
        church_id: asset.church_id,
        asset_id: assetId,
        depreciation_date: currentDate.toISOString().split('T')[0],
        fiscal_year: currentDate.getFullYear(),
        fiscal_period: currentDate.getMonth() + 1,
        opening_value: currentValue,
        depreciation_amount: depAmount,
        accumulated_depreciation: accumulatedDep + depAmount,
        closing_value: currentValue - depAmount,
        status: 'scheduled'
      })

      accumulatedDep += depAmount
      currentValue -= depAmount

      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1)
    }

    // Insert schedule entries
    const { error } = await supabase
      .from('asset_depreciation_schedule')
      .insert(scheduleEntries)

    if (error) throw error
  }

  /**
   * Post monthly depreciation for a specific period
   */
  static async postDepreciation(
    churchId: string,
    fiscalYear: number,
    fiscalPeriod: number,
    userId: string
  ): Promise<number> {
    // Get all scheduled depreciation entries for the period
    const { data: schedules, error } = await supabase
      .from('asset_depreciation_schedule')
      .select('*, asset:fixed_assets(*)')
      .eq('church_id', churchId)
      .eq('fiscal_year', fiscalYear)
      .eq('fiscal_period', fiscalPeriod)
      .eq('status', 'scheduled')

    if (error) throw error

    let totalDepreciation = 0

    for (const schedule of schedules || []) {
      // Create journal entry for depreciation
      const journalEntry = await supabase
        .from('general_ledger')
        .insert({
          church_id: churchId,
          entry_date: schedule.depreciation_date,
          fiscal_year: fiscalYear,
          fiscal_period: fiscalPeriod,
          entry_type: 'journal',
          reference: `DEP-${schedule.asset.asset_code}`,
          description: `Depreciation for ${schedule.asset.asset_name}`,
          status: 'posted',
          created_by: userId,
          posted_at: new Date().toISOString(),
          posted_by: userId
        })
        .select()
        .single()

      if (journalEntry.error) throw journalEntry.error

      // Debit: Depreciation Expense
      await supabase
        .from('general_ledger_lines')
        .insert({
          church_id: churchId,
          entry_id: journalEntry.data.id,
          account_id: schedule.asset.depreciation_expense_account_id,
          line_type: 'debit',
          debit_amount: schedule.depreciation_amount,
          credit_amount: 0,
          description: `Depreciation expense for ${schedule.asset.asset_name}`
        })

      // Credit: Accumulated Depreciation
      await supabase
        .from('general_ledger_lines')
        .insert({
          church_id: churchId,
          entry_id: journalEntry.data.id,
          account_id: schedule.asset.accumulated_depreciation_account_id,
          line_type: 'credit',
          debit_amount: 0,
          credit_amount: schedule.depreciation_amount,
          description: `Accumulated depreciation for ${schedule.asset.asset_name}`
        })

      // Update schedule status
      await supabase
        .from('asset_depreciation_schedule')
        .update({
          status: 'posted',
          journal_entry_id: journalEntry.data.id,
          posted_at: new Date().toISOString(),
          posted_by: userId
        })
        .eq('id', schedule.id)

      // Update asset
      await supabase
        .from('fixed_assets')
        .update({
          accumulated_depreciation: schedule.accumulated_depreciation,
          current_value: schedule.closing_value,
          last_depreciation_date: schedule.depreciation_date,
          updated_at: new Date().toISOString()
        })
        .eq('id', schedule.asset_id)

      totalDepreciation += schedule.depreciation_amount
    }

    return totalDepreciation
  }

  /**
   * Dispose of an asset
   */
  static async disposeAsset(
    assetId: string,
    disposalData: {
      disposal_date: string
      disposal_method: DisposalMethod
      disposal_proceeds?: number
      disposal_notes?: string
    },
    userId: string
  ): Promise<FixedAsset> {
    const { data, error } = await supabase
      .from('fixed_assets')
      .update({
        ...disposalData,
        status: 'disposed',
        is_active: false,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', assetId)
      .select()
      .single()

    if (error) throw error

    // TODO: Create journal entry for asset disposal (gain/loss on disposal)

    return data
  }

  /**
   * Get depreciation schedule for an asset
   */
  static async getAssetDepreciationSchedule(assetId: string): Promise<DepreciationSchedule[]> {
    const { data, error } = await supabase
      .from('asset_depreciation_schedule')
      .select('*')
      .eq('asset_id', assetId)
      .order('depreciation_date')

    if (error) throw error
    return data || []
  }

  /**
   * Get maintenance log for an asset
   */
  static async getAssetMaintenanceLog(assetId: string): Promise<MaintenanceLog[]> {
    const { data, error } = await supabase
      .from('asset_maintenance_log')
      .select('*')
      .eq('asset_id', assetId)
      .order('maintenance_date', { ascending: false })

    if (error) throw error
    return data || []
  }

  /**
   * Add maintenance record
   */
  static async addMaintenanceRecord(
    churchId: string,
    maintenanceData: Omit<MaintenanceLog, 'id' | 'church_id' | 'created_at'>,
    userId: string
  ): Promise<MaintenanceLog> {
    const { data, error } = await supabase
      .from('asset_maintenance_log')
      .insert({
        church_id: churchId,
        ...maintenanceData,
        created_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Get asset summary by category
   */
  static async getAssetSummaryByCategory(churchId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('v_assets_register')
      .select('asset_category, purchase_cost, current_value, accumulated_depreciation')
      .eq('church_id', churchId)
      .eq('status', 'active')

    if (error) throw error

    // Group by category
    const summary = (data || []).reduce((acc: any, asset: any) => {
      if (!acc[asset.asset_category]) {
        acc[asset.asset_category] = {
          category: asset.asset_category,
          count: 0,
          total_cost: 0,
          total_current_value: 0,
          total_depreciation: 0
        }
      }

      acc[asset.asset_category].count++
      acc[asset.asset_category].total_cost += asset.purchase_cost
      acc[asset.asset_category].total_current_value += asset.current_value
      acc[asset.asset_category].total_depreciation += asset.accumulated_depreciation

      return acc
    }, {})

    return Object.values(summary)
  }

  /**
   * Get assets needing maintenance
   */
  static async getAssetsNeedingMaintenance(churchId: string): Promise<AssetSummary[]> {
    const today = new Date().toISOString().split('T')[0]

    const { data, error} = await supabase
      .from('v_assets_register')
      .select('*')
      .eq('church_id', churchId)
      .eq('status', 'active')
      .lte('next_maintenance_date', today)

    if (error) throw error
    return data || []
  }

  /**
   * Create purchase journal entry for cash/bank payment
   * DR Fixed Asset
   *   CR Cash/Bank
   */
  static async createPurchaseJournalEntry(asset: FixedAsset, userId: string): Promise<void> {
    if (!asset.bank_account_id) {
      throw new Error('Bank account is required for payment journal entry')
    }

    const purchaseDate = new Date(asset.purchase_date)
    const fiscalYear = purchaseDate.getFullYear()
    const fiscalPeriod = purchaseDate.getMonth() + 1

    // Create journal entry
    const { data: journalEntry, error: jeError } = await supabase
      .from('general_ledger')
      .insert({
        church_id: asset.church_id,
        entry_date: asset.purchase_date,
        fiscal_year: fiscalYear,
        fiscal_period: fiscalPeriod,
        entry_type: 'journal',
        reference: `ASSET-${asset.asset_code}`,
        description: `Purchase of ${asset.asset_name} - Paid by ${asset.payment_method}${asset.payment_reference ? ' - Ref: ' + asset.payment_reference : ''}`,
        status: 'posted',
        created_by: userId,
        posted_at: new Date().toISOString(),
        posted_by: userId
      })
      .select()
      .single()

    if (jeError) throw jeError

    // Debit: Fixed Asset Account
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: asset.asset_account_id,
        line_type: 'debit',
        debit_amount: asset.amount_paid || asset.purchase_cost,
        credit_amount: 0,
        description: `Purchase of ${asset.asset_name}`
      })

    // Credit: Cash/Bank Account
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: asset.bank_account_id,
        line_type: 'credit',
        debit_amount: 0,
        credit_amount: asset.amount_paid || asset.purchase_cost,
        description: `Payment for ${asset.asset_name}`
      })

    // Update asset with journal entry ID
    await supabase
      .from('fixed_assets')
      .update({ purchase_journal_entry_id: journalEntry.id })
      .eq('id', asset.id)
  }

  /**
   * Create purchase journal entry for credit purchase
   * DR Fixed Asset
   *   CR Accounts Payable
   */
  static async createCreditPurchaseJournalEntry(asset: FixedAsset, userId: string): Promise<void> {
    // Get the default Accounts Payable account
    const { data: apAccount, error: apError } = await supabase
      .from('chart_of_accounts')
      .select('id')
      .eq('church_id', asset.church_id)
      .eq('account_type', 'liability')
      .ilike('account_name', '%accounts%payable%')
      .limit(1)
      .single()

    if (apError || !apAccount) {
      throw new Error('Accounts Payable account not found. Please create an Accounts Payable account first.')
    }

    const purchaseDate = new Date(asset.purchase_date)
    const fiscalYear = purchaseDate.getFullYear()
    const fiscalPeriod = purchaseDate.getMonth() + 1

    // Create journal entry
    const { data: journalEntry, error: jeError } = await supabase
      .from('general_ledger')
      .insert({
        church_id: asset.church_id,
        entry_date: asset.purchase_date,
        fiscal_year: fiscalYear,
        fiscal_period: fiscalPeriod,
        entry_type: 'journal',
        reference: `ASSET-${asset.asset_code}`,
        description: `Purchase of ${asset.asset_name} on credit${asset.vendor_id ? ' from vendor' : ''}${asset.credit_terms ? ' - Terms: ' + asset.credit_terms : ''}`,
        status: 'posted',
        created_by: userId,
        posted_at: new Date().toISOString(),
        posted_by: userId
      })
      .select()
      .single()

    if (jeError) throw jeError

    // Debit: Fixed Asset Account
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: asset.asset_account_id,
        line_type: 'debit',
        debit_amount: asset.purchase_cost,
        credit_amount: 0,
        description: `Purchase of ${asset.asset_name}`
      })

    // Credit: Accounts Payable
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: apAccount.id,
        line_type: 'credit',
        debit_amount: 0,
        credit_amount: asset.purchase_cost,
        description: `Payable for ${asset.asset_name}`
      })

    // Update asset with journal entry ID
    await supabase
      .from('fixed_assets')
      .update({ purchase_journal_entry_id: journalEntry.id })
      .eq('id', asset.id)
  }

  /**
   * Record a payment for a credit purchase
   * DR Accounts Payable
   *   CR Cash/Bank
   */
  static async recordAssetPayment(
    assetId: string,
    paymentData: Omit<AssetPayment, 'id' | 'church_id' | 'asset_id' | 'created_at' | 'journal_entry_id'>,
    userId: string
  ): Promise<AssetPayment> {
    // Get asset details
    const asset = await this.getAsset(assetId)

    if (!paymentData.bank_account_id) {
      throw new Error('Bank account is required for payment')
    }

    // Get Accounts Payable account
    const { data: apAccount, error: apError } = await supabase
      .from('chart_of_accounts')
      .select('id')
      .eq('church_id', asset.church_id)
      .eq('account_type', 'liability')
      .ilike('account_name', '%accounts%payable%')
      .limit(1)
      .single()

    if (apError || !apAccount) {
      throw new Error('Accounts Payable account not found')
    }

    const paymentDate = new Date(paymentData.payment_date)
    const fiscalYear = paymentDate.getFullYear()
    const fiscalPeriod = paymentDate.getMonth() + 1

    // Create journal entry for payment
    const { data: journalEntry, error: jeError } = await supabase
      .from('general_ledger')
      .insert({
        church_id: asset.church_id,
        entry_date: paymentData.payment_date,
        fiscal_year: fiscalYear,
        fiscal_period: fiscalPeriod,
        entry_type: 'journal',
        reference: `PAY-${asset.asset_code}${paymentData.payment_reference ? '-' + paymentData.payment_reference : ''}`,
        description: `Payment for ${asset.asset_name}${paymentData.payment_reference ? ' - Ref: ' + paymentData.payment_reference : ''}`,
        status: 'posted',
        created_by: userId,
        posted_at: new Date().toISOString(),
        posted_by: userId
      })
      .select()
      .single()

    if (jeError) throw jeError

    // Debit: Accounts Payable
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: apAccount.id,
        line_type: 'debit',
        debit_amount: paymentData.payment_amount,
        credit_amount: 0,
        description: `Payment for ${asset.asset_name}`
      })

    // Credit: Cash/Bank Account
    await supabase
      .from('general_ledger_lines')
      .insert({
        church_id: asset.church_id,
        entry_id: journalEntry.id,
        account_id: paymentData.bank_account_id,
        line_type: 'credit',
        debit_amount: 0,
        credit_amount: paymentData.payment_amount,
        description: `Payment for ${asset.asset_name}`
      })

    // Record the payment
    const { data: payment, error: payError } = await supabase
      .from('asset_payments')
      .insert({
        church_id: asset.church_id,
        asset_id: assetId,
        ...paymentData,
        journal_entry_id: journalEntry.id,
        created_by: userId
      })
      .select()
      .single()

    if (payError) throw payError

    return payment
  }

  /**
   * Get payment history for an asset
   */
  static async getAssetPayments(assetId: string): Promise<AssetPayment[]> {
    const { data, error } = await supabase
      .from('asset_payments')
      .select('*')
      .eq('asset_id', assetId)
      .order('payment_date', { ascending: false })

    if (error) throw error
    return data || []
  }
}

export default AssetsService
